import { getFragmentData } from 'gql'
import { AuthUserFragmentFragmentDoc, ErrorFragmentFragmentDoc } from 'gql/graphql'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { authUserFromDTO } from 'features/user/api/authUser'
import { useAuthenticate } from 'features/user/hooks/useAuthenticate'
import { useAbility } from 'providers/casl'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { CONFIRM_USER } from './api/mutation'

import type { Schema } from '../../schema'
import type { UseConfirmUser } from './types'
import type { ConfirmUserMutation, ConfirmUserMutationVariables } from 'gql/graphql'
import type { FieldErrors } from 'react-hook-form'

export const useConfirmUser = (): UseConfirmUser => {
  const { t } = useTranslation('features/user')
  const { authenticate } = useAuthenticate()
  const { enqueueSnackbar } = useSnackbar()
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})
  const ability = useAbility()

  const { isPending, mutateAsync } = useMutation<ConfirmUserMutationVariables, ConfirmUserMutation>(
    {
      mutation: CONFIRM_USER,
      onCompleted: async ({ confirmUser }: ConfirmUserMutation): Promise<void> => {
        const accessToken = confirmUser?.accessToken
        const refreshToken = confirmUser?.refreshToken
        const errors = getFragmentData(ErrorFragmentFragmentDoc, confirmUser?.errors)
        const user = getFragmentData(AuthUserFragmentFragmentDoc, confirmUser?.user)

        if (errors && errors.length > 0) {
          setErrors(mapGraphQLErrors(errors))
        } else if (user && accessToken && refreshToken) {
          setErrors({})
          authenticate({ accessToken, refreshToken, user: authUserFromDTO(user) })
          enqueueSnackbar(t('completeSetup.form.success'), {
            variant: 'success',
          })
        }
      },
    },
  )

  const confirm = (data: Schema): void => {
    void mutateAsync({
      canAccessGamConnection: ability.can('access', 'GlobalSetupPage-googleAdManagerConnection'),
      input: data,
    })
  }

  return {
    confirm,
    errors,
    loading: isPending,
  }
}
